import { z } from 'zod';
// ============================================================================
// KIR-4854: Shadow AI tool approval
//
// An approval is per tool, for the whole organization. Browser AI Tools keep
// their own approvals (`browser`, keyed by domain / tool id); Installed AI
// Tools and Running Now share one (`endpoint`, keyed by the agent tool id), so
// approving Cursor in one tab approves it in the other.
// ============================================================================
export const TOOL_APPROVAL_SCOPES = {
    BROWSER: 'browser',
    ENDPOINT: 'endpoint',
};
export const toolApprovalScopeSchema = z.enum([
    TOOL_APPROVAL_SCOPES.BROWSER,
    TOOL_APPROVAL_SCOPES.ENDPOINT,
]);
/** The "Show" filter on each tab. */
export const TOOL_APPROVAL_FILTERS = {
    NOT_APPROVED: 'not_approved',
    APPROVED: 'approved',
    ALL: 'all',
};
export const toolApprovalFilterSchema = z.enum([
    TOOL_APPROVAL_FILTERS.NOT_APPROVED,
    TOOL_APPROVAL_FILTERS.APPROVED,
    TOOL_APPROVAL_FILTERS.ALL,
]);
/** Tabs whose Show-filter totals can be requested. */
export const TOOL_APPROVAL_TABS = {
    BROWSER: 'browser',
    INSTALLED: 'installed',
    RUNNING: 'running',
};
export const toolApprovalTabSchema = z.enum([
    TOOL_APPROVAL_TABS.BROWSER,
    TOOL_APPROVAL_TABS.INSTALLED,
    TOOL_APPROVAL_TABS.RUNNING,
]);
/**
 * Approval state attached to a list row. `null` means "not approved"; the field
 * is absent when the tool-approval feature flag is off.
 */
export const toolApprovalSchema = z.object({
    approvedAt: z.string().datetime({ offset: true }),
    // Null when the approving user was since removed (FK is ON DELETE SET NULL).
    approvedByName: z.string().nullable(),
});
// ============================================================================
// Write endpoints (approve / unapprove)
// ============================================================================
/** Upper bound per request; a page is at most 100 rows. */
export const TOOL_APPROVAL_MAX_TOOL_IDS = 100;
export const toolApprovalMutationBodySchema = z.object({
    scope: toolApprovalScopeSchema,
    toolIds: z
        .array(z.string().trim().min(1).max(512))
        .min(1)
        .max(TOOL_APPROVAL_MAX_TOOL_IDS),
});
export const toolApprovalMutationResponseSchema = z.object({
    // Tools whose state actually changed (already-approved / not-approved ids
    // are skipped, so a repeat click writes no duplicate audit entry).
    changed: z.number().int().min(0),
});
// ============================================================================
// Show-filter totals
// ============================================================================
export const toolApprovalCountsQuerySchema = z.object({
    tab: toolApprovalTabSchema,
    scopeUserId: z.string().uuid().optional(),
});
/**
 * KIR-4864: personal-account Show-filter totals. Always the caller's own rows,
 * so there is no user scope to pass.
 */
export const personalToolApprovalCountsQuerySchema = z.object({
    tab: toolApprovalTabSchema,
});
/**
 * Tab totals for the Show filter. Independent of the table's search and column
 * filters by design (product decision on KIR-4851).
 */
export const toolApprovalCountsResponseSchema = z.object({
    notApproved: z.number().int().min(0),
    approved: z.number().int().min(0),
    all: z.number().int().min(0),
});
/** Optional `approval` query param shared by the three org table endpoints. */
export const toolApprovalQueryParamSchema = toolApprovalFilterSchema.optional();
//# sourceMappingURL=shadow-ai-tool-approvals.js.map